import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { BLOCK_TYPES, PALETTE_DRAG_TYPE, getBlockSummary } from './blockCatalog';
import {
  BLOCK_WIDTH,
  CANVAS_EXTENT,
  DEFAULT_BLOCK_HEIGHT,
  clampZoom,
  clientToCanvas,
  getBranchOutputRatio,
} from './flowGeometry';
import ConnectionLayer from './ConnectionLayer';
import FlowBlockCard from './FlowBlockCard';
import ZoomControls from './ZoomControls';

const isEditableTarget = target => {
  if (!target || !target.tagName) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

// The pannable/zoomable automation canvas: renders all blocks and connections,
// handles block dragging, connection dragging with validation, palette drops,
// mouse-wheel zoom and SPACE/middle-mouse panning.
const FlowCanvas = forwardRef(function FlowCanvas(
  {
    blocks,
    connections,
    lists,
    selectedBlockId,
    selectedConnectionKey,
    readOnly,
    onSelectBlock,
    onSelectConnection,
    onClearSelection,
    setGraph,
    beginInteraction,
    onConnect,
    onDeleteBlock,
    onDeleteConnection,
    onAddBlockAt,
  },
  ref
) {
  const theme = useTheme();
  const viewportRef = useRef(null);
  const blockElsRef = useRef(new Map());
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [spaceDown, setSpaceDown] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [connDrag, setConnDrag] = useState(null);
  const [blockSizes, setBlockSizes] = useState({});

  // Mirror volatile values into refs so window-level pointer handlers stay fresh.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef(pan);
  panRef.current = pan;
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const connectionsRef = useRef(connections);
  connectionsRef.current = connections;
  const spaceDownRef = useRef(false);

  const registerBlockEl = useCallback((blockId, element) => {
    if (element) blockElsRef.current.set(blockId, element);
    else blockElsRef.current.delete(blockId);
  }, []);

  // Measure block heights whenever content that affects them changes so the
  // connection ports follow the rendered cards.
  useLayoutEffect(() => {
    const next = {};
    let changed = false;
    blockElsRef.current.forEach((element, blockId) => {
      if (!element) return;
      const height = element.offsetHeight;
      next[blockId] = height;
      if (blockSizes[blockId] !== height) changed = true;
    });
    if (!changed && Object.keys(next).length !== Object.keys(blockSizes).length) changed = true;
    if (changed) setBlockSizes(next);
  }, [blocks, lists, blockSizes]);

  /* ------------------------------- zooming ------------------------------- */

  const applyZoom = useCallback((nextZoom, viewportPoint) => {
    const previousZoom = zoomRef.current;
    const previousPan = panRef.current;
    const newZoom = clampZoom(nextZoom);
    let point = viewportPoint;
    if (!point) {
      const rect = viewportRef.current?.getBoundingClientRect();
      point = rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 };
    }
    const canvasX = (point.x - previousPan.x) / previousZoom;
    const canvasY = (point.y - previousPan.y) / previousZoom;
    setZoom(newZoom);
    setPan({ x: point.x - canvasX * newZoom, y: point.y - canvasY * newZoom });
  }, []);

  // Mouse wheel zooms centered on the cursor (non-passive to prevent scrolling).
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return undefined;
    const handleWheel = event => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      applyZoom(zoomRef.current * factor, {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    };
    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, [applyZoom]);

  const fitView = useCallback(() => {
    const element = viewportRef.current;
    if (!element) return;
    const list = blocksRef.current;
    const rect = element.getBoundingClientRect();
    if (list.length === 0) {
      setZoom(1);
      setPan({ x: 40, y: 40 });
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    list.forEach(block => {
      const height = blockElsRef.current.get(block.blockId)?.offsetHeight || DEFAULT_BLOCK_HEIGHT;
      minX = Math.min(minX, block.position.x);
      minY = Math.min(minY, block.position.y);
      maxX = Math.max(maxX, block.position.x + BLOCK_WIDTH);
      maxY = Math.max(maxY, block.position.y + height);
    });
    const margin = 60;
    const boundsWidth = Math.max(maxX - minX, 1);
    const boundsHeight = Math.max(maxY - minY, 1);
    const newZoom = clampZoom(
      Math.min((rect.width - margin * 2) / boundsWidth, (rect.height - margin * 2) / boundsHeight, 1)
    );
    setZoom(newZoom);
    setPan({
      x: (rect.width - boundsWidth * newZoom) / 2 - minX * newZoom,
      y: (rect.height - boundsHeight * newZoom) / 2 - minY * newZoom,
    });
  }, []);

  // Fit the loaded flow into view once on mount (the canvas mounts after the
  // flow has finished loading, so the first render already has all blocks).
  const didInitialFit = useRef(false);
  useEffect(() => {
    if (didInitialFit.current) return;
    didInitialFit.current = true;
    if (blocks.length > 0) fitView();
  }, [blocks, fitView]);

  useImperativeHandle(
    ref,
    () => ({
      fitView,
      centerOnBlock: blockId => {
        const block = blocksRef.current.find(entry => entry.blockId === blockId);
        const element = viewportRef.current;
        if (!block || !element) return;
        const rect = element.getBoundingClientRect();
        const currentZoom = zoomRef.current;
        const height = blockElsRef.current.get(blockId)?.offsetHeight || DEFAULT_BLOCK_HEIGHT;
        setPan({
          x: rect.width / 2 - (block.position.x + BLOCK_WIDTH / 2) * currentZoom,
          y: rect.height / 2 - (block.position.y + height / 2) * currentZoom,
        });
      },
      getViewportCenterCanvasPos: () => {
        const element = viewportRef.current;
        if (!element) return { x: 200, y: 150 };
        const rect = element.getBoundingClientRect();
        return clientToCanvas(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
          rect,
          panRef.current,
          zoomRef.current
        );
      },
    }),
    [fitView]
  );

  /* ------------------------------- panning ------------------------------- */

  // Track the SPACE key globally (ignored while typing in form fields).
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.code !== 'Space' || isEditableTarget(event.target)) return;
      event.preventDefault();
      if (!spaceDownRef.current) {
        spaceDownRef.current = true;
        setSpaceDown(true);
      }
    };
    const handleKeyUp = event => {
      if (event.code !== 'Space') return;
      spaceDownRef.current = false;
      setSpaceDown(false);
    };
    const handleBlur = () => {
      spaceDownRef.current = false;
      setSpaceDown(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const startPan = useCallback(event => {
    const startClient = { x: event.clientX, y: event.clientY };
    const startPanPos = panRef.current;
    setIsPanning(true);
    const handleMove = moveEvent => {
      setPan({
        x: startPanPos.x + (moveEvent.clientX - startClient.x),
        y: startPanPos.y + (moveEvent.clientY - startClient.y),
      });
    };
    const handleUp = () => {
      setIsPanning(false);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }, []);

  const handleViewportPointerDown = useCallback(
    event => {
      if (event.button === 1 || (event.button === 0 && spaceDownRef.current)) {
        event.preventDefault();
        startPan(event);
        return;
      }
      if (event.button === 0) onClearSelection();
    },
    [onClearSelection, startPan]
  );

  /* ----------------------------- block dragging ----------------------------- */

  const handleBlockPointerDown = useCallback(
    (event, block) => {
      if (event.button !== 0 || spaceDownRef.current) return;
      event.stopPropagation();
      onSelectBlock(block.blockId);
      if (readOnly) return;
      const startClient = { x: event.clientX, y: event.clientY };
      const origin = { ...block.position };
      let moved = false;
      const handleMove = moveEvent => {
        if (!moved) {
          if (
            Math.abs(moveEvent.clientX - startClient.x) < 3 &&
            Math.abs(moveEvent.clientY - startClient.y) < 3
          ) {
            return;
          }
          moved = true;
          beginInteraction();
        }
        const position = {
          x: Math.round(origin.x + (moveEvent.clientX - startClient.x) / zoomRef.current),
          y: Math.round(origin.y + (moveEvent.clientY - startClient.y) / zoomRef.current),
        };
        setGraph(
          graph => ({
            ...graph,
            blocks: graph.blocks.map(entry =>
              entry.blockId === block.blockId ? { ...entry, position } : entry
            ),
          }),
          { record: false }
        );
      };
      const handleUp = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
      };
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [beginInteraction, onSelectBlock, readOnly, setGraph]
  );

  /* --------------------------- connection dragging --------------------------- */

  // True when the given output (a named branch, or the single unnamed output) is
  // already connected.
  const isOutputTaken = (fromId, branch) =>
    connectionsRef.current.some(
      connection => connection.from === fromId && (connection.branch || null) === (branch || null)
    );

  const finishConnection = useCallback(
    (fromId, branch, event) => {
      const target = document.elementFromPoint(event.clientX, event.clientY);
      const portEl = target ? target.closest('[data-port]') : null;
      const blockEl = target ? target.closest('[data-block-id]') : null;
      // Dropped on empty canvas: cancel silently.
      if (!blockEl) return;
      const toId = blockEl.getAttribute('data-block-id');
      if (portEl && portEl.getAttribute('data-port') === 'output') {
        // Releasing on the own output port is just a cancelled drag.
        if (toId === fromId) return;
        toast.warning('Ungültige Verbindung');
        return;
      }
      if (toId === fromId) {
        toast.warning('Ungültige Verbindung');
        return;
      }
      const targetBlock = blocksRef.current.find(entry => entry.blockId === toId);
      if (!targetBlock) return;
      if (BLOCK_TYPES[targetBlock.type]?.isStart) {
        toast.warning('Der Startblock kann keine eingehende Verbindung haben');
        return;
      }
      if (isOutputTaken(fromId, branch)) {
        toast.warning(
          branch ? 'Dieser Ausgang ist bereits verbunden' : 'Dieser Block hat bereits eine Verbindung'
        );
        return;
      }
      if (connectionsRef.current.some(connection => connection.to === toId)) {
        toast.warning('Dieser Eingang ist bereits verbunden');
        return;
      }
      onConnect(fromId, toId, branch);
    },
    [onConnect]
  );

  const handleOutputPointerDown = useCallback(
    (event, block, branch = null) => {
      if (event.button !== 0) return;
      event.stopPropagation();
      if (readOnly) return;
      if (isOutputTaken(block.blockId, branch)) {
        toast.warning(
          branch ? 'Dieser Ausgang ist bereits verbunden' : 'Dieser Block hat bereits eine Verbindung'
        );
        return;
      }
      const element = blockElsRef.current.get(block.blockId);
      const height = element ? element.offsetHeight : DEFAULT_BLOCK_HEIGHT;
      const from = {
        x: block.position.x + BLOCK_WIDTH * getBranchOutputRatio(branch),
        y: block.position.y + height,
      };
      const toCanvasPoint = pointerEvent => {
        const rect = viewportRef.current.getBoundingClientRect();
        return clientToCanvas(
          pointerEvent.clientX,
          pointerEvent.clientY,
          rect,
          panRef.current,
          zoomRef.current
        );
      };
      setConnDrag({ fromId: block.blockId, branch, from, to: from });
      const handleMove = moveEvent => {
        setConnDrag(previous => (previous ? { ...previous, to: toCanvasPoint(moveEvent) } : previous));
      };
      const handleUp = upEvent => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        setConnDrag(null);
        finishConnection(block.blockId, branch, upEvent);
      };
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [finishConnection, readOnly]
  );

  const handleInputPointerDown = useCallback(
    (event, block) => {
      if (event.button !== 0) return;
      event.stopPropagation();
      if (readOnly) return;
      toast.warning('Verbindungen müssen an einem Ausgang beginnen');
      onSelectBlock(block.blockId);
    },
    [onSelectBlock, readOnly]
  );

  /* ----------------------------- palette drops ----------------------------- */

  const handleDragOver = useCallback(event => {
    if (event.dataTransfer.types.includes(PALETTE_DRAG_TYPE)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback(
    event => {
      const type = event.dataTransfer.getData(PALETTE_DRAG_TYPE);
      if (!type) return;
      event.preventDefault();
      const rect = viewportRef.current.getBoundingClientRect();
      const point = clientToCanvas(event.clientX, event.clientY, rect, panRef.current, zoomRef.current);
      onAddBlockAt(type, {
        x: Math.round(point.x - BLOCK_WIDTH / 2),
        y: Math.round(point.y - 20),
      });
    },
    [onAddBlockAt]
  );

  /* -------------------------------- rendering -------------------------------- */

  const dotColor = alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.14 : 0.18);

  return (
    <Box
      ref={viewportRef}
      onPointerDown={handleViewportPointerDown}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.default',
        cursor: isPanning ? 'grabbing' : spaceDown ? 'grab' : 'default',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: CANVAS_EXTENT,
          height: CANVAS_EXTENT,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }}
      >
        <ConnectionLayer
          blocks={blocks}
          connections={connections}
          blockSizes={blockSizes}
          selectedKey={selectedConnectionKey}
          liveLine={connDrag ? { from: connDrag.from, to: connDrag.to } : null}
          readOnly={readOnly}
          onSelect={onSelectConnection}
          onDelete={onDeleteConnection}
        />
        {blocks.map(block => {
          const def = BLOCK_TYPES[block.type];
          if (!def) return null;
          return (
            <FlowBlockCard
              key={block.blockId}
              block={block}
              def={def}
              summary={getBlockSummary(block, { lists })}
              selected={block.blockId === selectedBlockId}
              readOnly={readOnly}
              registerEl={registerBlockEl}
              onPointerDown={handleBlockPointerDown}
              onOutputPointerDown={handleOutputPointerDown}
              onInputPointerDown={handleInputPointerDown}
              onDelete={onDeleteBlock}
            />
          );
        })}
      </Box>
      <ZoomControls zoom={zoom} onZoomTo={applyZoom} onFit={fitView} />
    </Box>
  );
});

export default FlowCanvas;
